import { useState, useRef, useEffect } from 'react';
import { Mic, Square, Send, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const AudioRecorder = ({ onAudioSend, disabled }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isSending, setIsSending] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });
      
      streamRef.current = stream;
      
      // Usar tipo MIME compatible con más navegadores
      let options = { mimeType: 'audio/webm' };
      
      // Fallback para Safari
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        options = { mimeType: 'audio/mp4' };
      }
      
      mediaRecorderRef.current = new MediaRecorder(stream, options);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: options.mimeType });
        setAudioBlob(audioBlob);
        
        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= 120) { // Max 2 minutes
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);

      toast.success('🎤 Grabación iniciada');
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('No se pudo acceder al micrófono. Verifica los permisos.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      toast.info('Grabación finalizada. Puedes escuchar o enviar.');
    }
  };

  const cancelRecording = () => {
    if (isRecording) {
      stopRecording();
    }
    
    setAudioBlob(null);
    setRecordingTime(0);
    audioChunksRef.current = [];
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    toast.info('Grabación cancelada');
  };

  const sendAudio = async () => {
    if (!audioBlob) return;

    setIsSending(true);
    
    try {
      await onAudioSend(audioBlob);
      
      // Reset state after successful send
      setAudioBlob(null);
      setRecordingTime(0);
      audioChunksRef.current = [];
      
    } catch (error) {
      console.error('Error sending audio:', error);
      toast.error('Error al enviar el audio');
    } finally {
      setIsSending(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Si hay un audio grabado pero no enviado
  if (audioBlob && !isRecording) {
    return (
      <div className="flex items-center gap-2 p-3 bg-teal-500/10 rounded-lg border border-teal-500/30 animate-in fade-in slide-in-from-bottom-2">
        <audio 
          controls 
          src={URL.createObjectURL(audioBlob)} 
          className="flex-1 h-8"
          style={{ maxWidth: '200px' }}
        />
        <span className="text-sm text-gray-400 min-w-[60px]">
          {formatTime(recordingTime)}
        </span>
        <Button
          onClick={sendAudio}
          disabled={isSending || disabled}
          size="sm"
          className="bg-teal-600 hover:bg-teal-700"
          title="Enviar audio"
        >
          {isSending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
        </Button>
        <Button
          onClick={cancelRecording}
          disabled={isSending}
          size="sm"
          variant="ghost"
          className="text-red-400 hover:text-red-300"
          title="Cancelar"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  // Si está grabando
  if (isRecording) {
    return (
      <div className="flex items-center gap-3 p-3 bg-red-500/10 rounded-lg border border-red-500/30 animate-pulse">
        <div className="flex items-center gap-2 flex-1">
          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
          <span className="text-sm font-medium text-red-400">
            Grabando...
          </span>
          <span className="text-sm text-gray-400">
            {formatTime(recordingTime)}
          </span>
        </div>
        <Button
          onClick={stopRecording}
          size="sm"
          variant="destructive"
          title="Detener grabación"
        >
          <Square className="w-4 h-4" />
        </Button>
        <Button
          onClick={cancelRecording}
          size="sm"
          variant="ghost"
          className="text-gray-400 hover:text-white"
          title="Cancelar"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  // Botón inicial para empezar a grabar
  return (
    <Button
      onClick={startRecording}
      disabled={disabled}
      size="icon"
      variant="ghost"
      className="text-teal-400 hover:text-teal-300 hover:bg-teal-500/10"
      title="Grabar audio"
    >
      <Mic className="w-5 h-5" />
    </Button>
  );
};

export default AudioRecorder;